<template>
  <tr
    v-for="(vehicle, index) in vehicles"
    :id="`row-tr-${index}`"
    :key="vehicle.id"
    class="border-bottom position-relative"
    :class="{ 'd-none': (foundVehicles.length > 0 && !foundVehicles.map(fV => fV.id).includes(vehicle.id)) || ($parent.isSearchVisible && $parent.searchText !== '' && !foundVehicles.length), 'd-initial': foundVehicles.length === 0 }"
  >
    <td class="calendar-cell">
      <VehicleCell :index="index" :vehicle="vehicle"/>
    </td>
    <td class="calendar-cell" style="max-width: 200px;">
      <TrailerCell
        v-if="vehicle.semiTrailer && showTrailers"
        :trailer="vehicle.semiTrailer"
        :vehicle="vehicle"
        :with-plate-number="false"
      />
      <div v-show="!vehicle.semiTrailer">
        <AttachTrailer :vehicle-id="vehicle.id"/>
      </div>
    </td>
    <td class="position-relative calendar-cell" :class="{ 'edge-cell': !isHorizontallyExpanded }">
      <div class="d-flex align-items-center" :class="{ 'justify-content-center': !vehicle.drivers.length, 'justify-content-between': vehicle.drivers.length }">
        <DriverCell
          v-if="vehicle.drivers.length > 0 && showDrivers"
          :drivers="vehicle.drivers"
          :vehicle="vehicle"
          :row-id="index"
        />
        <AttachDriver
          v-show="drivers.length > 0"
          :drivers="drivers"
          :vehicle="vehicle"
        />
      </div>
    </td>
    <td v-show="isHorizontallyExpanded" class="calendar-cell" :class="{ 'edge-cell': isHorizontallyExpanded }">
      <ModeCell :veh="vehicle" />
    </td>
    <td class="calendar-cell" :class="{ 'd-none': !activeDates.includes(cellDate) }" v-for="cellDate in dates" :key="cellDate">
      <div v-if="activeDates.includes(cellDate)" :class="{'weekend-cell': new Date(cellDate).getDay() === 6 || new Date(cellDate).getDay() === 0 }">
        <TaskCell
          :tasks="vehicle.tasksByDate[`${cellDate}T00:00:00`]"
          :vehicle-id="vehicle.id"
          :row-id="index"
          :date="cellDate"
          v-if="showTasks"
        />

        <div class="d-flex justify-content-center">
          <CellComments v-if="showComments" :comments="cellComments" :date="cellDate" :vehicle-id="vehicle.id"/>

          <div>
            <AutomaticLabels
              :date="cellDate"
              :vehicle-id="vehicle.id"
              v-if="showLabels"
            />
          </div>
        </div>
      </div>
    </td>
    <div v-show="$parent.rowId === index && $parent.isMapVisible" :id="`map-container-${index}`" class="row position-absolute" style="min-width: 1500px; left: 0; bottom: 0;">
      <RouteMap :index="index"/>
      <StatsChart :fulltime="0" :task="$parent.currentTask" period="24" :row-id="$parent.rowId"/>
    </div>
  </tr>
</template>

<script>
import TrailerCell from '@/components/TrailerCell'
import VehicleCell from '@/components/VehicleCell'
import DriverCell from '@/components/DriverCell'
import ModeCell from '@/components/ModeCell'
import TaskCell from '@/components/TaskCell'
import AutomaticLabels from '@/components/AutomaticLabels'
import AttachTrailer from '@/components/TasksCalendar/AttachTrailer.vue'
import axios from 'axios'
import AttachDriver from '@/components/TasksCalendar/AttachDriver'
import CellComments from '@/components/TasksCalendar/CellComments'
import emitter from '@/modules/events/emitter'
import eventKinds from '@/modules/events/eventKinds'
import throughWaterAcronyms from '@/modules/map/throughWaterAcronyms'
import { mapActions, mapGetters } from 'vuex'
import StatsChart from '@/components/StatsChart.vue'
import RouteMap from '@/components/TasksCalendar/RouteMap.vue'
import { $axios } from '@/modules/http/axios'
import moment from 'moment-timezone'

export default {
  name: 'VehicleOrdered',
  components: {
    RouteMap, StatsChart, CellComments, AttachDriver, TrailerCell, VehicleCell, DriverCell, AttachTrailer, ModeCell, TaskCell, AutomaticLabels
  },
  props: ['isHorizontallyExpanded', 'dates'],
  data () {
    return {
      foundVehicles: [],
      vehicles: [],
      automaticLabels: [],
      cellComments: [],
      axiosConfig: { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } },
      vehiclesChunkSize: 15,
      vehiclesPage: 0,
      loadingPartially: false,
      throughWaterAcronyms: throughWaterAcronyms,
      controller: new AbortController(),
      eventKinds: eventKinds,
      pollingVehicles: null,
      pollingLabels: null,
      pollingCellComments: null,
      showTasks: false,
      showLabels: false,
      showComments: false,
      showTrailers: false,
      showDrivers: false
    }
  },

  created () {
    this.setListeners()
    this.fetchVehiclesConfiguration()
    this.fetchPermissions()
    this.fetchVehicles()
    this.fetchLabels()
    this.fetchCellComments()
    this.fetchTrailers()
    this.fetchDrivers()
    this.fetchMailings()

    this.$store.dispatch('vehiclesModule/fetchVehicles')

    this.pollVehicles()
    this.pollLabels()
    this.pollCellComments()

    setTimeout(() => this.showTrailers = true, 10000)
    setTimeout(() => this.showDrivers = true, 12000)
    setTimeout(() => this.showTasks = true, 14000)
    setTimeout(() => this.showLabels = true, 17000)
    setTimeout(() => this.showComments = true, 20000)

    setTimeout(this.callFetchSearchableVehicles, 15000)
  },

  computed: {
    ...mapGetters({
      drivers: 'getCalendarDrivers',
      trailers: 'getCalendarTrailers',
      mailings: 'getCalendarMailings',
      initialCalendarLoading: 'getInitialCalendarLoading',
      searchableVehicles: 'getSearchableVehicles',
      vehiclesConfiguration: 'webConfigVehicles/getVehiclesConfiguration',
      activeDates: 'getCalendarActiveDates'
    })
  },

  beforeUnmount () {
    clearInterval(this.pollingVehicles)
    clearInterval(this.pollingLabels)
    clearInterval(this.pollingCellComments)
  },

  methods: {
    ...mapActions({
      fetchTrailers: 'fetchTrailers',
      fetchAutomaticLabels: 'automaticLabelsModule/fetchLabels',
      fetchVehiclesConfiguration: 'webConfigVehicles/fetchVehiclesConfiguration',
      fetchPermissions: 'permissionModule/fetchPermissions'
    }),

    callFetchSearchableVehicles () {
      const date = new Date(this.activeDates[0])
      let firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0]
      let lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 1).toISOString().split('T')[0]

      this.$store.dispatch('fetchSearchableVehicles', {
        start: firstDay,
        end  : lastDay
      })
    },

    setListeners () {
      emitter.on('planCommentAdded', () => {
        this.fetchCellComments()
      })

      emitter.on('planCommentDeleted', this.fetchCellComments)
      emitter.on(eventKinds.tasksListShown, () => {
        this.controller.abort()
      })
      emitter.on(eventKinds.storehouseShown, () => {
        this.controller.abort()
      })
      emitter.on(eventKinds.driverListingShown, () => {
        this.controller.abort()
      })
      emitter.on(eventKinds.placesShown, () => {
        this.controller.abort()
      })
      emitter.on(eventKinds.reportsShown, () => {
        this.controller.abort()
      })
      emitter.on(eventKinds.usersShown, () => {
        this.controller.abort()
      })
      emitter.on('searchClosed', () => this.foundVehicles = [])
      emitter.on('trioCommentModified', () => {
        if (this.$parent.isSearchVisible) {
          this.fetchVehicles().then(() => this.filterVehicles(this.$parent.searchText.trim()))
        } else {
          this.fetchVehicles()
        }
      })
      emitter.on('vehicleChanged', () => this.fetchVehicles())
      emitter.on('trailerDetached', () => {
        this.fetchVehicles()
        $axios.get('Trailer').then(res => {
          const trailers = res.data
          this.$store.commit('setCalendarTrailers', trailers)
        }).catch((e) => {
          console.log(e)
        })
      })
      emitter.on(eventKinds.trailerAttached, () => {
        $axios.get('Trailer').then(res => {
          const trailers = res.data
          this.$store.commit('setCalendarTrailers', trailers)
        }).catch((e) => {
          console.log(e)
        })
      })
      emitter.on('calendarRefreshed', () => {
        this.fetchVehiclesConfiguration().then(this.fetchVehicles).then(this.fetchCellComments).then(this.fetchLabels)
      })
      emitter.on(eventKinds.calendarMoved, e => {
        this.showLabels = false
        this.showComments = false
        if (!e.isSearchVisible) {
          this.fetchVehicles(e.dates)
        }

        this.fetchLabels(e.dates)
        this.fetchCellComments(e.dates)

        setTimeout(() => {
          this.showLabels = true
        }, 4000)

        setTimeout(() => {
          this.showComments = true
        }, 5000)
      })

      emitter.on('automaticLabelAdded', this.fetchLabels)
      emitter.on('vehiclesLoaded', this.fetchLabels)
      emitter.on('searchTyped', e => this.filterVehicles(e.searchText))
      emitter.on(eventKinds.calendarTaskUpdated, this.fetchVehicles)

      emitter.on(eventKinds.driverAttached, this.fetchVehicles)
      emitter.on(eventKinds.driverColorChanged, this.fetchVehicles)
      emitter.on(eventKinds.driverCommentAdded, this.fetchVehicles)
      emitter.on(eventKinds.driverCommentDeleted, this.fetchVehicles)

      emitter.on(eventKinds.vehicleCommentDeleted, this.fetchVehicles)
      emitter.on(eventKinds.vehicleCommentAdded, this.fetchVehicles)

      emitter.on(eventKinds.trailerCommentDeleted, this.fetchVehicles)
      emitter.on(eventKinds.trailerCommentAdded, this.fetchVehicles)
    },

    pollVehicles () {
      this.pollingVehicles = setInterval(() => {
        this.fetchVehicles()
        emitter.emit(eventKinds.vehiclesPolled)
      }, 22000)
    },

    pollLabels () {
      this.pollingLabels = setInterval(() => {
        this.fetchLabels()
      }, 30000)
    },

    pollCellComments () {
      this.pollingCellComments = setInterval(() => {
        this.fetchCellComments()
      }, 24000)
    },

    async fetchMailings () {
      if (this.mailings.length === 0) {
        const mailings = (await $axios.get('NotificationRecipient')).data
        await this.$store.commit('setCalendarMailings', mailings)
      }
    },

    async fetchVehicles (changedDates = []) {
      const dates = this.activeDates

      const order = {}
      this.vehiclesConfiguration.forEach((c, i) => order[c] = i)

      if (dates[0] && dates[dates.length - 1]) {
        const fromDate = moment(changedDates.length > 0 ? changedDates[0] : dates[0]).subtract(1, 'day').format('YYYY-MM-DD')
        const toDate = moment(dates[dates.length - 1]).add(1, 'day').format('YYYY-MM-DD')

        this.vehicles = (await $axios.get(`Task/Vehicles/Filtered/Dates?fromDate=${fromDate}&toDate=${toDate}`)).data.sort((a, b) => order[a.id] - order[b.id])
      }
    },

    async fetchDrivers () {
      if (this.drivers.length === 0) {
        const drivers = (await axios.get(`${process.env.VUE_APP_API_BASE_URL}Driver`, this.axiosConfig)).data
        await this.$store.commit('setCalendarDrivers', drivers)
      }
    },

    async fetchLabels (changedDates = []) {
      const dates = this.activeDates

      if (dates[0] && dates[dates.length - 1]) {
        const fromDate = moment(changedDates.length > 0 ? changedDates[0] : dates[0]).subtract(1, 'day').format('YYYY-MM-DD')
        const toDate = moment(dates[dates.length - 1]).add(1, 'day').format('YYYY-MM-DD')

        await this.fetchAutomaticLabels({
          fromDateString: fromDate,
          toDateString: toDate,
          vehicles: this.vehicles
        })
      }
    },

    async fetchCellComments (changedDates = []) {
      const dates = this.activeDates

      if (dates[0] && dates[dates.length - 1]) {
        const fromDate = moment(changedDates.length > 0 ? changedDates[0] : dates[0]).subtract(1, 'day').format('YYYY-MM-DD')
        const toDate = moment(dates[dates.length - 1]).add(1, 'day').format('YYYY-MM-DD')

        this.cellComments = (await axios.get(`${process.env.VUE_APP_API_BASE_URL}Comment/${fromDate}/${toDate}`, this.axiosConfig)).data
      }
    },

    filterVehicles (searchText) {
      this.searchText = searchText

      if (searchText.includes(',')) {
        this.foundVehicles = []
        const searchTextParts = searchText.split(',')

        searchTextParts.map(part => {
          const found = this.searchableVehicles.filter(sV => sV.plateNumber.toLowerCase().includes(part.toLowerCase().trim()))

          found.map(f => this.foundVehicles.push(f))
        })
      } else {
        this.foundVehicles = this.searchableVehicles.filter(sV =>
          sV.plateNumber.toLowerCase().includes(searchText.toLowerCase()) ||
          sV.semiTrailer?.plateNumber?.toLowerCase()?.includes(searchText.toLowerCase()) ||
          sV.drivers.find(driver => driver.firstName.toLowerCase().includes(searchText.toLowerCase()) || driver.surname.toLowerCase().includes(searchText.toLowerCase()))
        )

        if (this.foundVehicles.length === 0) {
          this.foundVehicles = this.searchableVehicles.filter(sV =>
            Object.keys(sV.tasksByDate)?.find(dateSetKey => sV.tasksByDate[dateSetKey].find(task => {
              if (task.numer_Przejazdu.toLowerCase().includes(searchText.toLowerCase())) {
                this.$parent.date = task.zaladunek.split('T')[0]
                this.$parent.populateUI('none')
                this.fetchVehicles()
                return true
              } else {
                return false
              }
            }))
          )
        }
      }
    }
  }
}
</script>

<style scoped>
.weekend-cell {
  background-color: #ffe0cc !important;
  height: 100%;
  border: 2px solid #ffffff;
  border-radius: 6px;
}

.calendar-cell {
  max-width: 200px;
  padding: 0;
  height: 100%;
}

td {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  border-right: 1px solid #000000;
  border-bottom: 1px solid #000000;
}

td.edge-cell {
  border-right: 4px solid #000000;
}
</style>
